import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function PWAManager() {
  const location = useLocation();

  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    
    // 1. Update Manifest & Meta tags dynamically for active SPA view using a safe singular injection function
    const injectManifestMetaTag = (targetManifest: string) => {
      const existingManifests = document.querySelectorAll('link[rel="manifest"]');
      console.log(`[PWA Manager] Current manifests in DOM: ${existingManifests.length}. Ensuring only one exists with href: ${targetManifest}`);
      
      let primaryManifest: HTMLLinkElement | null = null;
      
      existingManifests.forEach((el, index) => {
        const link = el as HTMLLinkElement;
        if (index === 0) {
          primaryManifest = link;
        } else {
          console.warn('[PWA Manager] Removing conflicting duplicate manifest element:', link.getAttribute('href'));
          link.remove();
        }
      });

      if (!primaryManifest) {
        console.log('[PWA Manager] No manifest found in DOM. Injecting new dynamic manifest.');
        primaryManifest = document.createElement('link');
        primaryManifest.rel = 'manifest';
        primaryManifest.id = 'pwa-manifest';
        document.head.appendChild(primaryManifest);
      }

      if (primaryManifest.id !== 'pwa-manifest') {
        primaryManifest.id = 'pwa-manifest';
      }

      if (primaryManifest.getAttribute('href') !== targetManifest) {
        console.log(`[PWA Manager] Injecting target manifest path: ${targetManifest}`);
        primaryManifest.setAttribute('href', targetManifest);
      }
    };

    const targetManifest = isAdmin ? '/admin/manifest.json' : '/manifest.json';
    injectManifestMetaTag(targetManifest);

    const themeEl = document.getElementById('pwa-theme');
    const appleTitleEl = document.getElementById('pwa-apple-title');
    const appleIconEl = document.getElementById('pwa-apple-icon');
    
    const targetTheme = isAdmin ? '#2A5431' : '#0047AB';
    const targetAppleTitle = isAdmin ? 'Raj Admin' : 'Raj Store';
    const targetTitle = isAdmin ? 'Raj Kirana Admin' : 'Raj Kirana Store';
    if (themeEl && themeEl.getAttribute('content') !== targetTheme) {
      themeEl.setAttribute('content', targetTheme);
    }
    if (appleTitleEl && appleTitleEl.getAttribute('content') !== targetAppleTitle) {
      appleTitleEl.setAttribute('content', targetAppleTitle);
    }
    if (document.title !== targetTitle) {
      document.title = targetTitle;
    }

    // 2. Hot-swap / Register appropriate Service Worker based on route
    if ('serviceWorker' in navigator) {
      const swPath = isAdmin ? '/admin/service-worker.js' : '/service-worker.js';
      const scope = isAdmin ? '/admin/' : '/';
      
      navigator.serviceWorker.register(swPath, { scope })
        .then((registration) => {
          console.log(`[PWA Manager] Registered Service Worker for scope ${scope}:`, registration.scope);
          
          // Dispatch custom event if a waiting SW is found
          if (registration.waiting) {
            window.dispatchEvent(new CustomEvent('sw-update-available', { detail: registration }));
          }
          
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  window.dispatchEvent(new CustomEvent('sw-update-available', { detail: registration }));
                }
              });
            }
          });
        })
        .catch((err) => {
          console.error('[PWA Manager] Service Worker registration failed:', err);
        });
    }
  }, [location.pathname]);

  return null;
}
